/**
 * RecentOrdersList — Recent Sales Orders for dashboard left column
 *
 * Renders each recent order as a single dense ROW — the seller-center vocabulary
 * of /orders?view=simple (OrderCardList) flattened from a stacked mini-card into
 * one horizontal band per order so the wide dashboard column reads full, not
 * empty. Each row packs, left→right: customer avatar, customer name + channel
 * badge, an order-id · time · item-count meta line, a product-thumbnail strip
 * (× qty), a payment-aware status badge, and the grand total.
 *
 * It borrows the shared visual tokens (OrderStatusBadge, ChannelBadge) and the
 * line-items + customer-avatar batch-fetch patterns from OrderCardList (the
 * shared useOrderBatch hooks). Glance-only: shipping address/phone, the full
 * payment-slip bar, and the action buttons are dropped — tapping the row still
 * opens the full order at /orders?detail=<name>.
 *
 * Paid/Unpaid is classified by the doctype-native payment signal
 * (`advance_paid >= grand_total`) — the same `isPaid` definition used by the
 * generic order list (buildListColumnsFromSchema) and order cards. It is NOT
 * classified by ERPNext workflow status: an order can be paid while still
 * sitting in "To Deliver and Bill" (paid, awaiting shipment). Classifying by
 * status froze the Paid tab at the last date a Sales Invoice was raised, hiding
 * every genuinely-paid storefront order that hadn't been billed yet.
 */

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ImageIcon, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { frappeListWithPagination } from "@/lib/erp/utils";
import { useAuthStore } from "@/lib/store/authStore";
import { OrderStatusBadge } from "@/features/orders/components/OrderStatusBadge";
import {
  useOrderItemsBatch,
  useCustomerAvatarsBatch,
  type OrderItemRow,
} from "@/features/orders/hooks/useOrderBatch";
import { ChannelBadge } from "@/features/channels/components/ChannelBadge";
import { CARD } from "@/config/design-tokens";

// ── Types ──

interface OrderRow {
  name: string;
  customer?: string;
  customer_name?: string;
  creation?: string;
  grand_total?: number;
  advance_paid?: number;
  total_qty?: number;
  currency?: string;
  status?: string;
  per_delivered?: number;
  source_channel?: string;
  shopee_order_status?: string;
  lazada_order_status?: string;
  custom_refund_status?: string;
}

// ── Orders hooks ──

const ORDER_FIELDS = [
  "name",
  "customer",
  "customer_name",
  "creation",
  "grand_total",
  "advance_paid",
  "total_qty",
  "currency",
  "status",
  "per_delivered",
  "source_channel",
  "shopee_order_status",
  "lazada_order_status",
  "custom_refund_status",
];

type OrderSubTab = "paid" | "unpaid";

/** Channels that carry a recognizable icon in ChannelBadge — only these get a
 *  badge on the row; storefront/LINE orders stay clean (no plain-text chip). */
const MARKETPLACE_CHANNELS = new Set(["Lazada", "Shopee", "TikTok Shop"]);

const PER_TAB = 10;

/**
 * Buffers sized so each tab independently returns its own recent {@link PER_TAB}
 * without starving the minority class.
 *
 * The old design fetched ONE fixed 50-order buffer and partitioned it in
 * memory, so a tab could be starved: if the last 50 orders were almost all
 * paid, the Unpaid tab showed too few (or none), and vice-versa.
 *
 * - PAID: pre-filtered server-side by `advance_paid > 0`. That is a provable
 *   *exact superset* of paid (isPaidOrder needs advance_paid >= grand_total > 0,
 *   which forces advance_paid > 0), so this never drops a paid order and the
 *   massive population of never-paid orders is excluded at the DB. A small
 *   buffer therefore always yields the recent PER_TAB paid orders regardless of
 *   how many unpaid orders were placed recently. isPaidOrder still runs to drop
 *   the rare partial payment (0 < advance_paid < grand_total).
 * - UNPAID: unpaid means `advance_paid < grand_total`, a two-field comparison
 *   Frappe list filters can't express, so there is no exact single-field server
 *   filter (an `advance_paid = 0` filter would wrongly hide partially-paid
 *   orders). We keep the exact `!isPaidOrder` partition and fetch a larger
 *   recent buffer as the operator-approved fallback. Starves only if the last
 *   UNPAID_BUFFER submitted orders contain fewer than PER_TAB unpaid ones — an
 *   exact per-tab fix here would need a backend endpoint doing the field
 *   comparison.
 */
const PAID_BUFFER = 40;
const UNPAID_BUFFER = 200;

/** Canonical payment signal — matches `isPaid` in buildListColumnsFromSchema. */
function isPaidOrder(o: OrderRow): boolean {
  const gt = Number(o.grand_total ?? 0);
  const adv = Number(o.advance_paid ?? 0);
  return gt > 0 && adv >= gt;
}

/**
 * Recent PAID orders — server-filtered by `advance_paid > 0` (exact superset of
 * paid), then refined by the canonical isPaidOrder. Independent of unpaid volume.
 */
function usePaidRecentOrders() {
  const tenant = useAuthStore((s) => s.currentTenant);

  return useQuery({
    queryKey: ["dashboard-recent-orders", "paid", tenant],
    queryFn: async () => {
      const res = await frappeListWithPagination<OrderRow>("Sales Order", {
        fields: ORDER_FIELDS,
        filters: [
          ["docstatus", "=", 1],
          ["advance_paid", ">", 0],
        ],
        order_by: "creation desc",
        limit_page_length: PAID_BUFFER,
        limit_start: 0,
      });
      return (res.data ?? []).filter(isPaidOrder).slice(0, PER_TAB);
    },
    enabled: !!tenant,
    staleTime: 60_000,
  });
}

/**
 * Recent UNPAID orders — larger recent buffer partitioned by the exact
 * `!isPaidOrder` signal (no single-field server filter can express
 * advance_paid < grand_total without hiding partial payments).
 */
function useUnpaidRecentOrders() {
  const tenant = useAuthStore((s) => s.currentTenant);

  return useQuery({
    queryKey: ["dashboard-recent-orders", "unpaid", tenant],
    queryFn: async () => {
      const res = await frappeListWithPagination<OrderRow>("Sales Order", {
        fields: ORDER_FIELDS,
        filters: [["docstatus", "=", 1]],
        order_by: "creation desc",
        limit_page_length: UNPAID_BUFFER,
        limit_start: 0,
      });
      return (res.data ?? []).filter((o) => !isPaidOrder(o)).slice(0, PER_TAB);
    },
    enabled: !!tenant,
    staleTime: 60_000,
  });
}

// Line-items and customer-avatar batch fetching are shared with OrderCardList
// via features/orders/hooks/useOrderBatch — ONE implementation, ONE cache
// (query keys order-items-batch / customer-images-batch). Imported above.

// ── Helpers ──

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  // Use calendar days, not 24-hour periods — an order at 23:00 yesterday
  // is only 16h ago at 15:00 today but should show "เมื่อวาน", not "วันนี้"
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const orderDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today.getTime() - orderDay.getTime()) / 86_400_000);
  const time = d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });

  if (diffDays === 0) return `วันนี้ ${time}`;
  if (diffDays === 1) return `เมื่อวาน ${time}`;
  if (diffDays < 7) return `${diffDays} วันที่แล้ว ${time}`;

  const date = d.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
  return `${date} ${time}`;
}

function formatAmount(amount?: number, currency?: string): string {
  if (amount == null) return "—";
  const sym = currency === "USD" ? "$" : "฿";
  return `${sym}${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ── Order row ──

const MAX_THUMBS = 3;

/** Single product thumbnail with a corner "× qty" badge — shown on every
 *  item (×1, ×2, …) so the per-line quantity always reads at a glance. */
function ItemThumb({ item }: { item: OrderItemRow }) {
  return (
    <div className="relative flex-shrink-0">
      {item.image ? (
        <img
          src={item.image}
          alt={item.item_name}
          className="w-9 h-9 rounded-md object-cover bg-muted"
          loading="lazy"
        />
      ) : (
        <div className="w-9 h-9 rounded-md bg-muted/60 flex items-center justify-center">
          <ImageIcon className="w-4 h-4 text-muted-foreground/40" />
        </div>
      )}
      {item.qty > 0 && (
        <span className="absolute -bottom-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-foreground/85 text-background text-[9px] font-semibold leading-4 text-center tabular-nums">
          {`×${item.qty}`}
        </span>
      )}
    </div>
  );
}

/** Compact, glance-only single row — a flattened OrderCardList entry. */
function RecentOrderRow({
  order,
  items,
  avatarUrl,
}: {
  order: OrderRow;
  items: OrderItemRow[];
  avatarUrl?: string;
}) {
  const marketplaceStatus = order.shopee_order_status || order.lazada_order_status;
  const channel = order.source_channel ?? "";
  const showChannel = MARKETPLACE_CHANNELS.has(channel);
  const totalQty =
    order.total_qty != null && order.total_qty > 0
      ? Number(order.total_qty)
      : items.reduce((sum, it) => sum + it.qty, 0);

  return (
    <Link
      to={`/orders?detail=${order.name}`}
      className={cn(
        "flex items-center gap-3 sm:gap-4 rounded-xl px-3.5 py-3",
        "border border-neutral-200 dark:border-neutral-800",
        "bg-white dark:bg-neutral-950 transition-all duration-150",
        "hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-sm"
      )}
    >
      {/* Customer avatar */}
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          className="w-9 h-9 rounded-full object-cover bg-muted flex-shrink-0"
          loading="lazy"
        />
      ) : (
        <div className="w-9 h-9 rounded-full bg-muted/60 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-muted-foreground/40" />
        </div>
      )}

      {/* Customer name + order-id · time · qty meta line */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[13px] font-medium text-foreground truncate">
            {order.customer_name || order.name}
          </span>
          {showChannel && (
            <span className="flex-shrink-0">
              <ChannelBadge channel={channel} size={14} />
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="font-mono truncate max-w-[120px]">{order.name}</span>
          <span className="text-neutral-300 dark:text-neutral-700">{"·"}</span>
          <span className="whitespace-nowrap">{formatDateTime(order.creation)}</span>
          <span className="text-neutral-300 dark:text-neutral-700">{"·"}</span>
          <span className="whitespace-nowrap">{`${totalQty} รายการ`}</span>
        </div>
      </div>

      {/* Product thumbnail strip — first to drop on narrow widths */}
      {items.length > 0 && (
        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
          {items.slice(0, MAX_THUMBS).map((it, idx) => (
            <ItemThumb key={idx} item={it} />
          ))}
          {items.length > MAX_THUMBS && (
            <div className="w-9 h-9 rounded-md bg-muted/60 flex items-center justify-center text-[11px] font-medium text-muted-foreground flex-shrink-0">
              {`+${items.length - MAX_THUMBS}`}
            </div>
          )}
        </div>
      )}

      {/* Payment-aware status badge + grand total */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0 text-right">
        <OrderStatusBadge
          status={order.status ?? ""}
          perDelivered={order.per_delivered ?? 0}
          marketplaceStatus={marketplaceStatus}
          paymentVerified={isPaidOrder(order)}
          refundStatus={order.custom_refund_status}
        />
        <span className="text-[14px] font-semibold tabular-nums text-foreground">
          {formatAmount(order.grand_total, order.currency)}
        </span>
      </div>
    </Link>
  );
}

function SkeletonCards() {
  return (
    <div className="space-y-2.5 px-4">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 rounded-xl border border-neutral-200 dark:border-neutral-800 px-3.5 py-3 animate-pulse"
        >
          <div className="w-9 h-9 rounded-full bg-muted flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 bg-muted rounded" />
            <div className="h-2.5 w-44 bg-muted rounded" />
          </div>
          <div className="hidden sm:flex gap-1.5">
            <div className="w-9 h-9 rounded-md bg-muted" />
            <div className="w-9 h-9 rounded-md bg-muted" />
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="h-5 w-20 bg-muted rounded-full" />
            <div className="h-3 w-14 bg-muted rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Export ──

export function RecentOrdersList() {
  const [subTab, setSubTab] = useState<OrderSubTab>("paid");
  const { data: paidOrders, isLoading: paidLoading } = usePaidRecentOrders();
  const { data: unpaidOrders, isLoading: unpaidLoading } = useUnpaidRecentOrders();

  const orders = subTab === "paid" ? paidOrders ?? [] : unpaidOrders ?? [];
  const isLoading = subTab === "paid" ? paidLoading : unpaidLoading;

  const orderNames = useMemo(() => orders.map((o) => o.name), [orders]);
  const customerKeys = useMemo(
    () => [...new Set(orders.map((o) => o.customer ?? "").filter(Boolean))],
    [orders]
  );

  const { data: allItems } = useOrderItemsBatch(orderNames);
  const { data: avatars } = useCustomerAvatarsBatch(customerKeys);

  const itemsByOrder = useMemo(() => {
    const map = new Map<string, OrderItemRow[]>();
    for (const item of allItems ?? []) {
      const list = map.get(item.parent) ?? [];
      list.push(item);
      map.set(item.parent, list);
    }
    return map;
  }, [allItems]);

  return (
    <div className={cn(CARD, "shadow-none")}>
      {/* Header inside card — matches QuickActions / RecentChatList pattern */}
      <div className="flex items-center justify-between px-6 pt-6 pb-2">
        <h3 className="text-sm font-semibold text-foreground">
          {"คำสั่งซื้อล่าสุด"}
        </h3>
        <Link
          to="/orders"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {"ดูทั้งหมด"}
        </Link>
      </div>

      {/* Sub-tabs: paid / unpaid */}
      <div className="flex gap-1 p-1 mx-6 mb-3 bg-muted/50 rounded-lg w-fit">
        <button
          onClick={() => setSubTab("paid")}
          className={cn(
            "px-4 py-1.5 text-xs font-medium rounded-md transition-all",
            subTab === "paid"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {"ชำระแล้ว"}
        </button>
        <button
          onClick={() => setSubTab("unpaid")}
          className={cn(
            "px-4 py-1.5 text-xs font-medium rounded-md transition-all",
            subTab === "unpaid"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {"ยังไม่ชำระ"}
        </button>
      </div>

      {/* Order rows */}
      {isLoading ? (
        <div className="pb-4">
          <SkeletonCards />
        </div>
      ) : !orders.length ? (
        <div className="py-10 text-center">
          <p className="text-sm text-muted-foreground">
            {subTab === "paid"
              ? "ยังไม่มีคำสั่งซื้อที่ชำระแล้ว"
              : "ไม่มีคำสั่งซื้อที่รอชำระ"}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5 px-4 pb-4">
          {orders.map((order) => (
            <RecentOrderRow
              key={order.name}
              order={order}
              items={itemsByOrder.get(order.name) ?? []}
              avatarUrl={order.customer ? avatars?.[order.customer] : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
